import { useEffect, useState } from "react";

import type { CopyAgenticLogicRuleRequestBodyType } from "@intelligems/ig-types";
import { IgDialog, IgTextField, IgTypography } from "@intelligems/ig-theme";
import { Stack } from "@mui/material";

import { useCopyAgenticLogicRule } from "@src/api/agentic-logic/copy-agentic-logic-rule";

interface CopyRuleDialogProps {
  open: boolean;
  ruleId: string;
  /** The rule being forked, used to seed the copy's name. */
  ruleName: string;
  /** The live workspace to save on the copy. */
  draft: CopyAgenticLogicRuleRequestBodyType["draft"];
  onClose: () => void;
  onCopied: (ruleId: string) => void;
}

/**
 * "Save as new rule" — the caret's fork.
 *
 * It asks for a name and a description because a copy is a rule like any
 * other: it shows up in the rules list on its own, and an unnamed second
 * "PDP cross-sell" is indistinguishable from the one it came from. The live
 * workspace travels as one validated draft snapshot, so this choice never
 * falls back to stale saved code.
 */
export const CopyRuleDialog = ({
  open,
  ruleId,
  ruleName,
  draft,
  onClose,
  onCopied,
}: CopyRuleDialogProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const copyRule = useCopyAgenticLogicRule();

  // Seeded on open rather than at mount: the dialog outlives a rename, and a
  // stale suggestion would name the copy after what the rule used to be.
  useEffect(() => {
    if (!open) return;
    setName(`${ruleName} (copy)`.trim());
    setDescription("");
  }, [open, ruleName]);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();

  const handleCopy = async () => {
    if (!trimmedName || !trimmedDescription) return;
    const result = await copyRule.mutateAsync({
      body: {
        name: trimmedName,
        description: trimmedDescription,
        ...(draft ? { draft } : {}),
      },
      pathParams: { ruleId },
      queryParams: undefined,
    });
    onCopied(result.rule.id);
  };

  return (
    <IgDialog
      showDialog={open}
      title="Save as new rule"
      yesButtonText="Save as new rule"
      noButtonText="Cancel"
      yesButtonDisabled={
        !trimmedName || !trimmedDescription || copyRule.isPending
      }
      loading={copyRule.isPending}
      handleYesClick={handleCopy}
      handleNoClick={onClose}
      dialogContentWidth={"400px"}
      handleClose={onClose}
      content={
        <Stack spacing={2} sx={{ pt: 1, minWidth: 340 }}>
          <IgTypography variant="body2">
            The copy starts from this rule&apos;s last saved code and goes live
            on its own — it serves nothing until you assign it to a placement.
          </IgTypography>
          <IgTextField
            label="Name"
            value={name}
            autoFocus
            fullWidth
            onChange={(event) => setName(event.target.value)}
          />
          <IgTextField
            label="Description"
            value={description}
            fullWidth
            multiline
            maxRows={4}
            slotProps={{ htmlInput: { maxLength: 1024 } }}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Stack>
      }
    />
  );
};
